import { Sparkles, Trophy, Heart, Users } from "lucide-react";

const CRITERIA = [
  {
    icon: Users,
    title: "Community Trust",
    body: "Consistent five-star Google reviews from families across Linn County — spanning years of care.",
  },
  {
    icon: Heart,
    title: "Consistent Care",
    body: "Long-term relationships with residents and their families — many stay with us until end of life.",
  },
  {
    icon: Sparkles,
    title: "Quality of Life",
    body: "Cleanliness, nutrition, dignity, and a calm atmosphere — verified by independent reviewers.",
  },
];

export default function Awards() {
  return (
    <section
      id="awards"
      data-testid="awards-section"
      className="relative py-24 md:py-32 bg-[#1C2A24] overflow-hidden"
    >
      {/* soft radial glow */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 500px at 20% 30%, rgba(166,124,82,0.18), transparent 60%), radial-gradient(700px 400px at 85% 80%, rgba(102,123,104,0.22), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Badge */}
          <div className="lg:col-span-5 flex justify-center lg:justify-start">
            <AwardMedallion />
          </div>

          {/* Copy */}
          <div className="lg:col-span-7">
            <p
              data-testid="awards-eyebrow"
              className="reveal text-xs uppercase tracking-[0.3em] font-semibold text-[#A67C52]"
            >
              Recognition
            </p>
            <h2
              data-testid="awards-title"
              className="reveal font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white mt-4"
              style={{ transitionDelay: "80ms" }}
            >
              Top 3 in Linn County,
              <br />
              <span className="text-[#C9A24A]">awarded for endurance.</span>
            </h2>
            <p
              className="reveal mt-6 text-[17px] leading-relaxed text-[#D9CFBF] max-w-2xl"
              style={{ transitionDelay: "140ms" }}
            >
              In 2026, Carlene&rsquo;s Comfort Care was named a{" "}
              <strong className="text-white">BusinessRate Top 3 Award Winner</strong>{" "}
              for Assisted Living Facilities in Linn County, Oregon — selected
              in the <em>Endurance</em> category, powered by Google Reviews.
            </p>

            <div className="reveal mt-10 grid sm:grid-cols-3 gap-4" style={{ transitionDelay: "220ms" }}>
              {CRITERIA.map((c) => (
                <div
                  key={c.title}
                  data-testid={`award-criteria-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 backdrop-blur-sm hover:bg-white/[0.07] hover:border-white/20 transition"
                >
                  <c.icon className="w-5 h-5 text-[#C9A24A]" />
                  <h3 className="font-heading text-lg text-white mt-3">
                    {c.title}
                  </h3>
                  <p className="text-[13px] leading-relaxed text-[#A8B3AB] mt-2">
                    {c.body}
                  </p>
                </div>
              ))}
            </div>

            <div
              className="reveal mt-10 flex flex-wrap items-center gap-x-5 gap-y-3 text-[#A8B3AB] text-sm"
              style={{ transitionDelay: "300ms" }}
            >
              <span className="inline-flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2">
                <Trophy className="w-4 h-4 text-[#C9A24A]" />
                BusinessRate &middot; Top 3
              </span>
              <span className="inline-flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2">
                Powered by Google Reviews
              </span>
              <span className="inline-flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2">
                Linn County, Oregon &middot; 2026
              </span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ccc-spin-slow {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes ccc-shimmer {
          0%   { opacity: .25; transform: translate(-20%, -20%) rotate(25deg); }
          50%  { opacity: .55; transform: translate(10%, 10%)  rotate(25deg); }
          100% { opacity: .25; transform: translate(-20%, -20%) rotate(25deg); }
        }
      `}</style>
    </section>
  );
}

/* -------------------- Custom CSS/SVG Award Medallion -------------------- */

function AwardMedallion() {
  return (
    <div
      data-testid="award-medallion"
      className="reveal relative w-[300px] h-[300px] sm:w-[360px] sm:h-[360px] md:w-[420px] md:h-[420px]"
    >
      {/* Outer glow */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(circle at center, rgba(201,162,74,0.25), transparent 65%)",
        }}
      />

      {/* Slow rotating gold ring */}
      <div
        aria-hidden
        className="absolute inset-[4%] rounded-full"
        style={{
          background:
            "conic-gradient(from 0deg, #C9A24A 0deg, rgba(201,162,74,0.1) 70deg, #C9A24A 140deg, rgba(201,162,74,0.1) 210deg, #C9A24A 280deg, rgba(201,162,74,0.1) 350deg, #C9A24A 360deg)",
          mask:
            "radial-gradient(circle at center, transparent 62%, #000 63%, #000 66%, transparent 67%)",
          WebkitMask:
            "radial-gradient(circle at center, transparent 62%, #000 63%, #000 66%, transparent 67%)",
          animation: "ccc-spin-slow 32s linear infinite",
        }}
      />

      {/* Main disc */}
      <div className="absolute inset-[10%] rounded-full bg-gradient-to-br from-[#223a2e] via-[#162921] to-[#0d1a14] border border-[#C9A24A]/50 shadow-[0_30px_80px_-30px_rgba(201,162,74,0.4),inset_0_1px_0_rgba(255,255,255,0.08)] overflow-hidden">
        {/* Endurance ribbon (absolute left band) */}
        <div className="absolute left-0 top-0 bottom-0 w-[12%] flex items-center justify-center bg-gradient-to-b from-[#3fa57a] via-[#2d8b62] to-[#22714e] border-r border-black/30 shadow-[inset_-4px_0_8px_rgba(0,0,0,0.25)]">
          <span
            className="text-white font-semibold text-[9px] sm:text-[11px] tracking-[0.3em] uppercase"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            Endurance
          </span>
        </div>

        {/* Content — symmetric padding so text is visually centered in the disc */}
        <div className="absolute inset-0 px-[14%] py-[10%] flex flex-col items-center justify-between text-center">
          {/* Top: TOP 3 label */}
          <p className="font-heading text-[10px] sm:text-[11px] tracking-[0.4em] text-[#C9A24A] uppercase font-semibold">
            Top 3
          </p>

          {/* Stars */}
          <div className="flex justify-center gap-[5px] text-[#C9A24A]">
            {Array.from({ length: 5 }).map((_, i) => (
              <svg
                key={i}
                viewBox="0 0 24 24"
                className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-current"
                aria-hidden
              >
                <path d="M12 2.2l2.9 6.3 6.9.7-5.2 4.7 1.5 6.8L12 17.3 5.9 20.7l1.5-6.8L2.2 9.2l6.9-.7z" />
              </svg>
            ))}
          </div>

          {/* Year */}
          <p className="font-heading text-[2.6rem] sm:text-5xl md:text-6xl font-semibold text-white leading-none">
            2026
          </p>

          {/* Divider with AWARD WINNER */}
          <div className="flex items-center gap-2 w-full max-w-[200px]">
            <span className="h-px flex-1 bg-[#C9A24A]/50" />
            <span className="text-[8px] sm:text-[9px] tracking-[0.3em] text-[#C9A24A] uppercase font-semibold whitespace-nowrap">
              Award Winner
            </span>
            <span className="h-px flex-1 bg-[#C9A24A]/50" />
          </div>

          {/* BusinessRate plate */}
          <div className="px-4 py-2 sm:px-5 sm:py-2.5 rounded-md bg-gradient-to-b from-[#f2ead4] via-[#e0d1a8] to-[#b59e6f] border border-[#8a6d3c]/50 shadow-[0_3px_10px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.6)]">
            <p
              className="font-heading text-[#1C2A24] text-base sm:text-lg leading-none tracking-tight"
              style={{ fontWeight: 600 }}
            >
              Business<span className="italic">Rate</span>
            </p>
          </div>

          {/* Business name */}
          <p className="font-heading text-white text-[15px] sm:text-[17px] leading-tight">
            Carlene&rsquo;s Comfort Care
          </p>

          {/* Meta */}
          <div className="flex flex-col items-center gap-1">
            <p className="text-[8px] sm:text-[9px] tracking-[0.25em] text-[#A8B3AB] uppercase">
              Assisted Living &middot; Linn County, OR
            </p>
            <p className="text-[7px] sm:text-[8px] tracking-[0.22em] text-[#667B68] uppercase">
              Powered by Google Reviews
            </p>
          </div>
        </div>

        {/* Decorative mini-laurel at bottom (below all text, purely ornamental) */}
        <svg
          aria-hidden
          viewBox="0 0 200 40"
          className="absolute bottom-[6%] left-1/2 -translate-x-1/2 w-[40%] h-auto text-[#C9A24A]/70"
          fill="currentColor"
        >
          {[0, 1, 2, 3].map((i) => (
            <g key={`l-${i}`} transform={`translate(${80 - i * 18} 20) rotate(${-20 - i * 4})`}>
              <ellipse cx="0" cy="0" rx="8" ry="2.8" />
            </g>
          ))}
          {[0, 1, 2, 3].map((i) => (
            <g key={`r-${i}`} transform={`translate(${120 + i * 18} 20) rotate(${20 + i * 4})`}>
              <ellipse cx="0" cy="0" rx="8" ry="2.8" />
            </g>
          ))}
          <circle cx="100" cy="20" r="2.2" />
        </svg>
      </div>
    </div>
  );
}

function Laurel() { return null; }
